import type {
  Nullable,
  Nullish,
  PrimaryKey,
  SafeAny,
  UUIDString,
} from '@aigc/types';
import type { RefSelectProps, SelectProps } from 'antd';
import { Select as AntdSelect } from 'antd';
import { useMemo, type Ref } from 'react';

export interface LabeledValue<
  TValue = PrimaryKey | UUIDString,
  TLabel = string,
> {
  label: TLabel;

  value: TValue;
}

type StrictSelectProps<TValue> = Omit<
  SelectProps<
    Nullish<TValue | TValue[]> | LabeledValue<TValue> | LabeledValue<TValue>[]
  >,
  'mode' | 'value' | 'onChange' | 'labelInValue' | 'defaultValue'
> & {
  ref?: Ref<RefSelectProps>;
} & (
    | {
        labelInValue?: undefined | false;
        mode?: undefined;
        defaultValue?: TValue;
        value?: Nullish<TValue>;
        onChange?: (value: Nullable<TValue>) => void;
      }
    | {
        labelInValue?: undefined | false;
        mode: 'multiple' | 'tags';
        defaultValue?: TValue[];
        value?: Nullish<TValue[]>;
        onChange?: (value: Nullable<TValue[]>) => void;
      }
    | {
        labelInValue: true;
        mode?: undefined;
        defaultValue?: LabeledValue<TValue>;
        value?: Nullish<LabeledValue<TValue>>;
        onChange?: (value: Nullable<LabeledValue<TValue>>) => void;
      }
    | {
        labelInValue: true;
        mode: 'multiple' | 'tags';
        defaultValue?: LabeledValue<TValue>[];
        value?: Nullish<LabeledValue<TValue>[]>;
        onChange?: (value: Nullable<LabeledValue<TValue>[]>) => void;
      }
  );

function AiSelect<ValueType = number | string>(
  props: StrictSelectProps<ValueType>,
) {
  const { onChange: _onChange, value: _value, ...selectProps } = props;

  return (
    <AntdSelect
      placeholder="请选择"
      {...selectProps}
      /**
       * WHY: 当 `mode` 为多选的情况下，value 为 `null` 时，会莫名其妙多出来一个空选择项，这里
       * 当 value 为 `null` 的时候，内部自动转换为 `undefined`
       */
      value={useMemo(() => {
        if (selectProps.mode == null) {
          return _value;
        }

        if (_value === null) {
          return void 0;
        }

        return _value;
      }, [_value, selectProps.mode])}
      onChange={useMemo(() => {
        if (!_onChange) {
          return;
        }

        return (value: SafeAny) => {
          /**
           * 清空的时候需要使用 `null` 代替 `undefined`
           */
          _onChange(value ?? null);
        };
      }, [_onChange])}
    />
  );
}

export type { RefSelectProps } from 'antd';
export { AiSelect as Select, type StrictSelectProps as SelectProps };
